class Tabs extends HTMLElement {
  tabBtns = this.querySelectorAll('[data-custom-tab]') as NodeListOf<HTMLElement>;
  section = this.querySelector('#expandable-section') as HTMLElement | null;
  toggleBtn = this.querySelector('#expand-toggle') as HTMLElement | null;

  connectedCallback() {
    this.tabBtns.forEach((btnEl) => {
      const inputEl = btnEl.firstElementChild as HTMLInputElement | null;
      if (inputEl && inputEl.hasAttribute('checked')) {
        this.openTabContent(inputEl.value);
        if (inputEl.value === 'reviews') {
          this.hideToggleButton();
        }
      }

      btnEl.addEventListener('click', (event) => {
        const currentBtn = event.currentTarget as HTMLElement | null;
        const currentInput = currentBtn?.firstElementChild as HTMLInputElement | null;
        if (!currentBtn || !currentInput) return;

        this.resetRadioButtons();
        currentInput.setAttribute('checked', 'true');
        currentBtn.setAttribute('aria-selected', 'true');
        this.openTabContent(currentInput.value);

        if (currentInput.value === 'reviews') {
          this.increaseReviewSectionHeight();
          this.hideToggleButton();
        } else {
          this.resetReviewSectionHeight();
          this.showToggleButton();
        }
      });
    });

    // Listen for external requests to open the reviews tab (e.g., from Trustpilot rating click)
    document.addEventListener('open-reviews-tab', () => {
      this.openReviewsTabAndScroll();
    });
  }

  hideToggleButton() {
    this.toggleBtn?.classList.add('pointer-events-none');
    this.toggleBtn?.classList.remove('pointer-events-auto');
    this.toggleBtn?.classList.add('opacity-0');
    this.toggleBtn?.classList.remove('opacity-100');
  }

  showToggleButton() {
    this.toggleBtn?.classList.add('pointer-events-auto');
    this.toggleBtn?.classList.remove('pointer-events-none');
    this.toggleBtn?.classList.add('opacity-100');
    this.toggleBtn?.classList.remove('opacity-0');
  }

  resetRadioButtons() {
    this.tabBtns.forEach((btnEl) => {
      btnEl.setAttribute('aria-selected', 'false');
      const inputEl = btnEl.firstElementChild as HTMLInputElement | null;
      inputEl?.removeAttribute('checked');
    });
  }

  increaseReviewSectionHeight() {
    if (this.section) {
      this.section.style.height = '700px';
    }
  }

  resetReviewSectionHeight() {
    if (this.section) {
      this.section.style.height = '';
    }
  }

  openTabContent(value: string) {
    const contentPanels = this.querySelectorAll('[id*="tab-content-for"]');

    contentPanels.forEach((panel) => {
      panel?.classList.add('hidden');
      panel?.setAttribute('aria-expanded', 'false');
    });

    const tabContent = this.querySelector(`#tab-content-for-${value}`);
    tabContent?.classList.remove('hidden');
    tabContent?.classList.add('block');
    tabContent?.setAttribute('aria-expanded', 'true');
  }

  private openReviewsTabAndScroll() {
    // Select the reviews tab programmatically
    const reviewsBtn = Array.from(this.tabBtns).find((btnEl) => {
      const input = btnEl.firstElementChild as HTMLInputElement | null;
      return input?.value === 'reviews';
    }) as HTMLElement | undefined;

    this.resetRadioButtons();
    if (reviewsBtn && reviewsBtn.firstElementChild) {
      const input = reviewsBtn.firstElementChild as HTMLInputElement | null;
      input?.setAttribute('checked', 'true');
      reviewsBtn.setAttribute('aria-selected', 'true');
    }

    this.openTabContent('reviews');
    this.increaseReviewSectionHeight();
    this.hideToggleButton();

    // Smooth scroll to the reviews panel
    const reviewsPanel = this.querySelector('#tab-content-for-reviews') as HTMLElement | null;
    const OFFSET = 160; // adjust if header size changes
    if (reviewsPanel) {
      this.scrollToElementWithOffset(reviewsPanel, OFFSET);
    } else if (this.section) {
      this.scrollToElementWithOffset(this.section as unknown as HTMLElement, OFFSET);
    }
  }

  private scrollToElementWithOffset(element: HTMLElement, offset: number) {
    const elementTop = element.getBoundingClientRect().top + window.scrollY;
    const targetY = Math.max(0, elementTop - offset);
    window.scrollTo({ top: targetY, behavior: 'smooth' });
  }
}

if (!customElements.get('custom-tabs')) {
  customElements.define('custom-tabs', Tabs);
}
